import React, { useMemo, useState } from 'react';
import { Button } from 'rebass/styled-components';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cs from 'classnames';

import ModuleCommentsModal from 'features/submissions/components/organisms/ModuleCommentsModal';
import Tooltip from 'shared/components/atoms/Tooltip/NewTooltip';
import { Flex, Text } from 'shared/components/display';
import moduleReviewFooterStyles from 'shared/components/molecules/ModuleReviewFooter/moduleReviewFooterStyles';
import { Values } from 'shared/components/molecules/ModuleReviewForm/moduleReviewFormConfig';
import ModuleRevisionModal from 'shared/components/molecules/ModuleRevisionModal/ModuleRevisionModal';
import ModuleReviewFormModal from 'shared/components/organisms/ModuleReviewFormModal';

import { SUBMISSION_COPY } from 'shared/config/copy';
import { intl } from 'shared/lib/intl';
import { RevisionStatus } from 'shared/typings/revisions';
import { ReviewModule } from 'shared/typings/submissions';
import { convertWidthNum } from 'shared/utilities/moduleReviewUtility';

interface Props {
  handleApproveModule: () => void;
  handleCriticalErrors: (args: { data: Values; errorCategory: string }) => void;
  numModulesRemaining: number;
  setOpenRevision: (boolean) => void;
  openRevision: boolean;
  setOpenComments: (boolean) => void;
  openComments: boolean;
  mediaWidth: number;
  approvalLoading: boolean;
  brandName: string;
  mod: ReviewModule;
  enableModuleComments?: boolean;
}
const ModuleReviewFooter: React.FC<Props> = ({
  numModulesRemaining,
  handleApproveModule,
  handleCriticalErrors,
  setOpenRevision,
  mediaWidth,
  approvalLoading,
  setOpenComments,
  mod,
  brandName,
  openComments,
  openRevision,
  enableModuleComments,
}) => {
  const mediaStyle = useMemo(() => {
    //Change the margins depending on the video width and make sure footer has a minimum width to fit all buttons
    const width = convertWidthNum(mediaWidth < 380 ? 390 : mediaWidth);
    return {
      widthStyle: {
        width: width,
      },
    };
  }, [mediaWidth]);
  const [isOpen, setIsOpen] = useState(false);

  const toggleCommentsModal = () => {
    setOpenComments((prev) => !prev);
    setOpenRevision(false);
  };

  const toggleRevisionModal = () => {
    setOpenRevision((prev) => !prev);
    setOpenComments(false);
  };

  const toggleModal = () => {
    setOpenRevision(false);
    setOpenComments(false);
    setIsOpen((prev) => !prev);
  };

  const onSubmitCriticalErrors = (args) => {
    handleCriticalErrors(args);
    toggleModal();
  };

  const onApproveModule = () => {
    handleApproveModule();
    toggleModal();
  };

  const getRevisionCommentsCount = () => {
    return mod?.revisions?.filter((revision) => revision.status === RevisionStatus.DOING).length || 0;
  };

  return (
    <Flex
      className={cs('review-footer')}
      css={moduleReviewFooterStyles}
      onClick={(e) => e.stopPropagation()}
      style={mediaStyle.widthStyle}
    >
      <Flex className={cs('review-footer__caption-wrapper')}>
        <Text className="review-footer__caption-text" data-cy="review-footer__caption-text">
          {intl.formatMessage(
            { id: 'CAPTION_REVIEW_NAME' },
            {
              moduleNum: mod.moduleNum,
            },
          )}
        </Text>

        <ModuleRevisionModal
          revisions={mod.revisions}
          setOpenRevision={toggleRevisionModal}
          submissionModuleId={mod.id}
          moduleNum={mod.moduleNum}
          mediaWidth={mediaWidth}
          brandName={brandName}
          openRevision={openRevision}
        />

        {enableModuleComments && (
          <ModuleCommentsModal
            setOpen={toggleCommentsModal}
            submissionModuleId={mod.id}
            mediaWidth={mediaWidth}
            moduleNum={mod.moduleNum}
            comments={mod.comments}
            open={openComments}
            className="review-footer__icon"
          />
        )}

        <Text className="review-footer__caption-text-modules">
          {intl.formatMessage(
            {
              id: 'CAPTION_REVIEW_MODULES_REMAINING',
            },
            {
              numRemaining: numModulesRemaining,
            },
          )}
        </Text>
      </Flex>
      <Flex className="review-footer__button-wrapper">
        <Button
          onClick={onApproveModule}
          disabled={approvalLoading}
          className="review-footer__approve-btn"
          data-cy="review-footer__approve-btn"
        >
          {SUBMISSION_COPY.BUTTON_APPROVE_MODULE_WITH_REVISIONS}
        </Button>
        <Tooltip content="Revisions" placement="bottom-end" portalTarget="review-footer">
          <Button onClick={toggleModal} className="review-footer__neutral-btn">
            {SUBMISSION_COPY.BUTTON_ADD_REVISION_NOTES}
          </Button>
        </Tooltip>

        <Button
          onClick={toggleRevisionModal}
          className={cs('review-footer__revision-btn', {
            'review-footer__revision-btn--active': openRevision,
          })}
        >
          <FontAwesomeIcon icon={faTriangleExclamation} size="lg" />
          {<span className="review-footer__revision-badge">{getRevisionCommentsCount()}</span>}
        </Button>

        <ModuleReviewFormModal onSubmit={onSubmitCriticalErrors} onClose={toggleModal} isOpen={isOpen} />
      </Flex>
    </Flex>
  );
};

export default ModuleReviewFooter;
